import { getAuthToken, getStoredUser, getAuthHeaders, clearAuth } from '@/utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  User, Mail, Phone, Calendar, Building2, Briefcase, 
  Edit2, Check, X, LogOut, MapPin, Globe, FileText, Users, ArrowLeft
} from 'lucide-react';
import Nav from '@/components/Nav';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '@/context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPE_LABELS = {
  developer: 'Real Estate Developer',
  broker: 'Real Estate Broker',
  agent: 'Real Estate Agent',
  car_dealer: 'Luxury Car Dealer',
  yacht_dealer: 'Yacht & Marine Dealer',
  interior_designer: 'Interior Designer',
  other_business: 'Other Business'
};

const PartnerAccountPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        navigate('/partner/login');
        return;
      }
      const response = await axios.get(`${API}/partner/me`, {
        headers: getAuthHeaders()
      });
      setProfileData(response.data);
      setEditForm({
        contact_person: response.data.contact_person || response.data.name || '',
        phone: response.data.phone || '',
        website: response.data.website || '',
        address: response.data.address || ''
      });
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/partner/login');
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const token = getAuthToken();
      await axios.put(`${API}/partner/profile`, editForm, {
        headers: getAuthHeaders()
      });
      
      setProfileData(prev => ({ ...prev, ...editForm }));
      setEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/partner/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AE', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const getBusinessIcon = (type) => {
    switch (type) {
      case 'developer':
      case 'broker':
      case 'agent':
        return <Building2 className="w-6 h-6" />;
      default:
        return <Briefcase className="w-6 h-6" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading profile...</div>
      </div>
    );
  }

  const profile = profileData || {};

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      {/* Navigation */}
      <Nav variant="partner" showBack partnerLabel="Partner Portal" />

      <div className="max-w-[1000px] mx-auto px-8 py-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-playfair text-4xl font-semibold mb-2">Partner Account</h1>
          <p className="text-text-secondary">Manage your business profile and details</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Business Profile Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-playfair text-xl font-semibold">Business Profile</h2>
              {!editing ? (
                <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                  <Edit2 className="w-4 h-4 mr-1" /> Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                  <Button size="sm" onClick={handleSaveProfile} disabled={saving} className="bg-gold text-black">
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Business Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-gold/30 to-purple-500/30 rounded-2xl flex items-center justify-center text-gold">
                {getBusinessIcon(profile.business_type)}
              </div>
            </div>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-semibold">{profile.company_name || 'Company Name'}</h3>
              <p className="text-gold mt-1">{BUSINESS_TYPE_LABELS[profile.business_type] || profile.business_type}</p>
            </div>

            {editing ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-text-secondary">Contact Person</Label>
                  <Input 
                    value={editForm.contact_person}
                    onChange={(e) => setEditForm({ ...editForm, contact_person: e.target.value })}
                    className="bg-white/5 border-white/10 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-text-secondary">Phone</Label>
                  <Input 
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="bg-white/5 border-white/10 mt-1"
                    placeholder="+971 50 123 4567"
                  />
                </div>
                <div>
                  <Label className="text-text-secondary">Website</Label>
                  <Input 
                    value={editForm.website}
                    onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                    className="bg-white/5 border-white/10 mt-1"
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <Label className="text-text-secondary">Address</Label>
                  <Input 
                    value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    className="bg-white/5 border-white/10 mt-1"
                    placeholder="Business address"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-text-secondary">
                  <User className="w-4 h-4" />
                  <span className="text-sm">Contact: {profile.contact_person || profile.name || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-3 text-text-secondary">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">{profile.email}</span>
                </div>
                {profile.phone && (
                  <div className="flex items-center gap-3 text-text-secondary">
                    <Phone className="w-4 h-4" />
                    <span className="text-sm">{profile.phone}</span>
                  </div>
                )}
                {profile.website && (
                  <div className="flex items-center gap-3 text-text-secondary">
                    <Globe className="w-4 h-4" />
                    <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-sm text-gold hover:underline">
                      {profile.website}
                    </a>
                  </div>
                )}
                {profile.address && (
                  <div className="flex items-center gap-3 text-text-secondary">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{profile.address}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-text-secondary">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Partner since {formatDate(profile.created_at)}</span>
                </div>
              </div>
            )}
          </motion.div>

          {/* Application & Business Details */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            {/* Application Status */}
            <div className="glass-card p-6">
              <h3 className="font-playfair text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gold" />
                Partnership Status
              </h3>
              <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/30 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-400">Active Partner</p>
                    <p className="text-text-secondary text-sm">Your partnership is active and in good standing</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Details from Application */}
            <div className="glass-card p-6">
              <h3 className="font-playfair text-lg font-semibold mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-gold" />
                Business Details
              </h3>
              <div className="space-y-4">
                {profile.company_registration && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-text-secondary text-sm">Registration Number</p>
                    <p className="font-medium">{profile.company_registration}</p>
                  </div>
                )}
                
                {profile.years_in_business && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-text-secondary text-sm">Years in Business</p>
                    <p className="font-medium">{profile.years_in_business} years</p>
                  </div>
                )}
                
                {profile.portfolio_value && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-text-secondary text-sm">Portfolio Value</p>
                    <p className="font-medium text-gold">AED {(profile.portfolio_value / 1000000).toFixed(0)}M+</p>
                  </div>
                )}

                {profile.specializations?.length > 0 && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-text-secondary text-sm mb-2">Specializations</p>
                    <div className="flex flex-wrap gap-2">
                      {profile.specializations.map((spec, idx) => (
                        <span key={`area-${idx}`} className="px-2 py-1 bg-gold/20 text-gold rounded text-xs">
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {profile.description && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-text-secondary text-sm mb-1">About</p>
                    <p className="text-sm">{profile.description}</p>
                  </div>
                )}
              </div>
            </div>

            {/* CRM Stats */}
            <div className="glass-card p-6">
              <h3 className="font-playfair text-lg font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-gold" />
                Performance Overview
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gold">{profile.total_leads || 0}</p>
                  <p className="text-text-secondary text-sm">Total Leads</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-400">{profile.converted_leads || 0}</p>
                  <p className="text-text-secondary text-sm">Conversions</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <Button 
            onClick={() => navigate(`/partner/dashboard/${profile.business_type}`)}
            className="w-full bg-gold text-black hover:bg-gold-light h-14 text-lg"
          >
            Go to Dashboard
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default PartnerAccountPage;
